import { createTheme } from "@material-ui/core/styles";

import { breakpoints } from "./breakpoints";
import { overrides } from "./overrides";
import { props } from "./props";
import { shadows } from "./shadows";
import { typography } from "./typography";
import { darkVariant as variants } from "./variants";
import { zIndex } from "./zIndex";

import type { Shadows } from "@material-ui/core/styles/shadows";

/**
 * @deprecated Do not use it in the new code base.
 * @todo Remove it with material-ui library
 */
export const theme = createTheme({
  spacing: 4,
  breakpoints: breakpoints,
  overrides: overrides,
  props: props,
  typography: typography,
  shadows: shadows,
  body: variants.body,
  header: variants.header,
  palette: variants.palette,
  sidebar: variants.sidebar,
  zIndex: zIndex,
});

/**
 * @deprecated Do not use it in the new code base.
 * @todo Remove it with material-ui library
 */
export const oldComponentsTheme = createTheme({
  shadows: [
    "none", // 0
    "", // 1
    "0px 0px 1px rgba(32, 37, 43, 0.1), 0px 4px 8px rgba(32, 37, 43, 0.12);", // 2
  ] as unknown as Shadows,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1280,
      xl: 1920,
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
  },
  palette: {
    background: {
      default: "#F7F7F7",
    },
    battleshipGrey: {
      main: "#797c80",
    },
    blueGrey: {
      main: "#d2d3d5",
    },
    charcoalGrey: {
      main: "#363a40",
    },
    coolGrey: {
      main: "#a6a8aa",
    },
    crayon: {
      main: "#8397aa",
    },
    dark: {
      main: "#20252b",
    },
    error: {
      main: "#DB1C1C",
    },
    gunmetal: {
      main: "#4d5155",
    },
    highlightPaper: {
      main: "#C6D8EA",
    },
    highlightPencil: {
      main: "#61768b",
    },
    ice: {
      main: "#e8e9e9",
    },
    navyCharcoal: {
      main: "#353843",
    },
    paper: {
      main: "#f7f7f7",
    },
    pastel: {
      main: "#A5B0BB",
    },
    primary: {
      main: "#1A63AB",
    },
    secondary: {
      main: "#1EA8A3",
    },
    spindle: {
      main: "#b5c8da",
    },
    success: {
      main: "#1EA8A3",
    },
    warning: {
      main: "#FFC66F",
    },
    water: {
      dark: "#c0d2e4",
      main: "#f3f8fc",
    },
    watercolor: {
      main: "#e8e9e9",
    },
    white: {
      main: "#ffffff",
    },
    grey: {
      600: "#a6a8aa",
      700: "#797c80",
    },
  },
  typography: {
    fontSize: 14,
    fontFamily: [
      "Lota Grotesque",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    body1: {
      fontSize: 14,
      fontWeight: "normal",
    },
    body2: {
      fontSize: 14,
      fontWeight: 600,
    },
    button: {
      lineHeight: 1,
      fontWeight: 700,
      letterSpacing: 1.6,
    },
    h1: {
      fontWeight: 600,
      fontSize: 64,
    },
    h2: {
      fontWeight: 600,
      fontSize: 32,
    },
    h3: {
      fontWeight: 600,
      fontSize: 24,
    },
    h4: {
      fontWeight: 600,
      fontSize: 16,
    },
    h5: {
      fontWeight: 700,
      fontSize: 14,
    },
    h6: {
      fontWeight: 700,
      fontSize: 12,
    },
  },
  overrides: {
    MuiCssBaseline: {
      "@global": {
        body: {
          fontFamily: [
            "Lota Grotesque",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(","),
        },
        "*::-webkit-scrollbar": {
          width: 4,
        },
        "*::-webkit-scrollbar-thumb": {
          borderRadius: 100,
          backgroundColor: "#d2d3d5",
        },
      },
    },
    MuiPopover: {
      root: {
        "& .MuiPaper-root": {
          // theme.shadows(2)
          boxShadow:
            "0px 0px 1px rgba(32, 37, 43, 0.1), 0px 4px 8px rgba(32, 37, 43, 0.12);",
          borderRadius: 4,
        },
      },
    },
    MuiPickersCalendar: {
      week: {
        "& div": {
          border: "1px solid #e8e9e9",
          borderRightWidth: 0,
          borderBottomWidth: 0,
          "&:last-child": {
            borderRight: "1px solid #e8e9e9",
          },
        },
        "&:last-child": {
          "& div": {
            borderBottom: "1px solid #e8e9e9",
          },
        },
      },
      transitionContainer: {
        minHeight: "auto",
        height: "100%",
        maxHeight: "215px",
        marginTop: "6px",
        marginBottom: "16px",
        overflow: "hidden",
        "&& > div": { position: "static" },
      },
    },
    MuiPickersCalendarHeader: {
      daysHeader: {
        padding: "0 10px",
      },
      switchHeader: {
        marginTop: 8,
        marginBottom: 16,
      },
      dayLabel: {
        margin: 0,
        fontSize: "12px",
        fontWeight: "bold",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "19px",
        letterSpacing: 1,
        textAlign: "center",
        color: "#797c80",
      },
    },
    MuiPickersBasePicker: {
      pickerView: {
        maxWidth: 245,
        minWidth: 245,
        minHeight: "unset",
      },
    },
    MuiPickersDay: {
      day: {
        borderRadius: 0,
        margin: 0,
        color: "#20252b",
        width: 32,
        height: 32,
        letterSpacing: 1,
        "& p": {
          fontSize: "12px",
          fontWeight: "normal",
          lineHeight: "1.58",
        },
      },
      daySelected: {
        borderRadius: 0,
        "& p": {
          fontWeight: "bold",
        },
      },
    },
    MuiTableRow: {
      root: {
        height: "100%",
      },
      head: {
        height: "100%",
      },
    },
    MuiDialog: {
      paper: {
        padding: "16px 32px",
        boxShadow: `0 10px 12px 0 rgba(32, 37, 43, 0.12),
                    0 0 1px 0 rgba(10, 31, 68, 0.1)`,
      },
    },
    MuiDialogTitle: {
      root: {
        padding: "16px 0",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: "rgba(0, 0, 0, 0.2)",
      },
    },
    MuiDialogContent: {
      root: {
        padding: "16px 0",
      },
    },
    MuiDialogActions: {
      root: {
        padding: "16px 0",
      },
    },
  },
});
